import { Link, useLocation } from "react-router-dom";
import SEO from "../components/SEO";
import PageHeader from "../components/PageHeader";
import Breadcrumbs, { buildBreadcrumbSchema } from "../components/Breadcrumbs";
import FollowTechniaHQ from "../components/FollowTechniaHQ";
import { ResponsiveComparisonTable } from "../components/ContentBlocks";
import { getAuthorityHubByPath } from "../data/authorityHubs";
import { seoConfig } from "../data/seoConfig";

function buildFaqSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map(([question, answer]) => ({
      "@type": "Question",
      name: question,
      acceptedAnswer: { "@type": "Answer", text: answer },
    })),
  };
}

function buildCollectionSchema(hub) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: hub.title,
    headline: hub.seoTitle,
    description: hub.metaDescription,
    url: `${seoConfig.baseUrl}${hub.path}`,
    dateModified: "2026-07-10",
    author: { "@type": "Organization", name: "TechniaHQRobot", url: `${seoConfig.baseUrl}/about-techniahq` },
    publisher: { "@id": `${seoConfig.baseUrl}/#organization` },
    mainEntity: hub.featuredArticles.map(([name, path]) => ({
      "@type": "Article",
      name,
      url: `${seoConfig.baseUrl}${path}`,
    })),
  };
}

export default function AuthorityHubPage() {
  const location = useLocation();
  const path = location.pathname.replace(/\/$/, "") || "/";
  const hub = getAuthorityHubByPath(path);

  if (!hub) {
    return (
      <section className="site-shell py-16">
        <div className="soft-card p-8">
          <h1 className="text-3xl font-black text-neutral-950">Robotics hub not found</h1>
          <Link to="/robotics" className="btn-primary mt-6">Open robotics guides</Link>
        </div>
      </section>
    );
  }

  const breadcrumbs = [
    { label: "Home", to: "/" },
    { label: hub.title, to: hub.path },
  ];

  return (
    <>
      <SEO
        title={hub.seoTitle}
        description={hub.metaDescription}
        path={hub.path}
        schema={[
          buildBreadcrumbSchema(breadcrumbs),
          buildCollectionSchema(hub),
          buildFaqSchema(hub.faq),
        ]}
      />
      <Breadcrumbs items={breadcrumbs} />
      <PageHeader eyebrow={hub.eyebrow} title={hub.title} description={hub.subtitle} />

      <div className="site-shell grid gap-10 py-10 sm:py-14">
        <section className="grid gap-5 lg:grid-cols-[minmax(0,1.2fr)_minmax(18rem,0.8fr)]">
          <div className="article-panel">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">Last updated {hub.updatedAt}</p>
            <div className="mt-5 grid gap-4 text-base leading-8 text-neutral-700">
              {hub.intro.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
            </div>
          </div>
          <aside className="rounded-[1.75rem] border border-blue-100 bg-blue-50 p-5 sm:p-7">
            <h2 className="text-xl font-black tracking-tight text-neutral-950">Evidence checklist</h2>
            <ul className="mt-4 grid gap-3 text-sm leading-7 text-neutral-700">
              {hub.keyFacts.map((fact) => <li key={fact} className="rounded-2xl bg-white p-4 shadow-sm">{fact}</li>)}
            </ul>
          </aside>
        </section>

        <section className="grid gap-5 lg:grid-cols-2">
          {hub.sections.map((section) => (
            <article key={section.heading} className="soft-card p-5 sm:p-7">
              <h2 className="text-2xl font-black tracking-tight text-neutral-950">{section.heading}</h2>
              <div className="mt-4 grid gap-4 text-base leading-8 text-neutral-700">
                {section.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
              </div>
            </article>
          ))}
        </section>

        {hub.tables.map((table) => (
          <section key={table.title} className="grid gap-4">
            <h2 className="text-3xl font-black tracking-tight text-neutral-950">{table.title}</h2>
            <ResponsiveComparisonTable
              columns={table.columns}
              rows={table.rows}
              caption={table.title}
              ariaLabel={table.title}
              minWidth="min-w-[760px]"
            />
          </section>
        ))}

        <section className="grid gap-5 lg:grid-cols-2">
          {hub.linkGroups.map((group) => (
            <div key={group.title} className="soft-card p-5 sm:p-7">
              <h2 className="text-2xl font-black tracking-tight text-neutral-950">{group.title}</h2>
              <div className="mt-5 grid gap-3">
                {group.links.map(([label, to]) => (
                  <Link key={to} to={to} className="rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm font-black text-neutral-800 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700">
                    {label}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </section>

        <section className="article-panel">
          <h2 className="text-3xl font-black tracking-tight text-neutral-950">Featured TechniaHQRobot reporting</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {hub.featuredArticles.map(([label, to]) => (
              <Link key={to} to={to} className="rounded-2xl border border-neutral-200 bg-neutral-50 p-5 text-base font-black leading-7 text-neutral-900 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700">
                {label}
              </Link>
            ))}
          </div>
        </section>

        <section className="article-panel">
          <h2 className="text-3xl font-black tracking-tight text-neutral-950">Frequently asked questions</h2>
          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            {hub.faq.map(([question, answer]) => (
              <article key={question} className="rounded-2xl border border-neutral-200 bg-neutral-50 p-5">
                <h3 className="text-lg font-black text-neutral-950">{question}</h3>
                <p className="mt-3 text-sm leading-7 text-neutral-700">{answer}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="rounded-[1.75rem] border border-neutral-200 bg-neutral-50 p-5 sm:p-7">
          <h2 className="text-3xl font-black tracking-tight text-neutral-950">Official sources and documentation</h2>
          <ul className="mt-5 grid gap-3 md:grid-cols-2">
            {hub.sources.map(([label, url]) => (
              <li key={url}>
                <a href={url} target="_blank" rel="noopener noreferrer" className="block rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm font-black text-neutral-800 transition hover:border-blue-200 hover:text-blue-700">
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <FollowTechniaHQ />
    </>
  );
}
